import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useContext } from 'react';
import SessionContext from '../contexts/SessionContext';
import API_URL from '../constants/environment';
import { OrderDetailResponse } from './useGetOrderDetail';
import { HomeFeedResponse } from './useHomeFeed';
import { OrderDrawerResponse } from './useGetOrderDrawer';

import { OrderFeedEntry } from './useGetOrdersFeed';

interface ArchiveOrderInput {
    orderId?: string;
    orderIds?: string[];
    archived?: boolean;
}

const archiveOrder = async (data: ArchiveOrderInput, sessionId: string | null) => {
    return axios.post(`${API_URL}/order/archive`, data, {
        headers: {
            Authorization: sessionId
        }
    });
};

export const useArchiveOrder = () => {
    const sessionContext = useContext(SessionContext);
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: ArchiveOrderInput) => archiveOrder(data, sessionContext?.sessionId ?? null),
        onMutate: async (variables) => {
            if (variables.orderIds?.length || !variables.orderId) return;

            await queryClient.cancelQueries({ queryKey: ['/orders/home-feed-paginated'] });
            await queryClient.cancelQueries({ queryKey: ['/dashboard/orders-feed-paginated'] });

            queryClient.setQueryData(
                ['/order/detail', variables.orderId],
                (old: OrderDetailResponse | undefined): OrderDetailResponse | undefined => {
                    if (!old) return old;
                    const archived = variables.archived ?? !old.archived;
                    return {
                        ...old,
                        archived,
                        completed: false
                    };
                }
            );
            queryClient.setQueryData<OrderDrawerResponse>(
                ['/order/drawer', variables.orderId],
                (old) => {
                    if (!old) return old;
                    const archived = variables.archived ?? !old.archived;
                    return {
                        ...old,
                        archived,
                        completed: false
                    };
                }
            );
            queryClient.setQueriesData<HomeFeedResponse>({ queryKey: ['/orders/home-feed-paginated'] }, (old) => {
                if (!old) return old;
                return {
                    ...old,
                    orders: old.orders.filter(order => order._id !== variables.orderId)
                };
            });
            queryClient.setQueriesData(
                { queryKey: ['/dashboard/orders-feed-paginated'] },
                (old: { orders: OrderFeedEntry[]; } | undefined) => {
                    if (!old || !old.orders) return old;
                    return {
                        ...old,
                        orders: old.orders.filter(order => order._id !== variables.orderId)
                    };
                }
            );
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['/orders/home-feed-paginated'] });
            queryClient.invalidateQueries({ queryKey: ['/dashboard/orders-feed-paginated'] });
        },
    });
};
